import { CtyButton, CtyShowToast, CtySpinner, JsonFormBuilder } from "@circuitry-ai/doc-ui";
import { useUploadFileToS3Mutation, useGetAiworkersPresignedUrlMutation, useUploadInternalActionMutation } from "@circuitry-ai/doc-data";
import { Icon } from "@iconify/react";
import { Input, Progress, Select, SelectItem } from "@nextui-org/react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

/* eslint-disable-next-line */
export interface ScoreProps {
  readonly isDisabled?: boolean;
}

const isArrayData = (data: any) => Array.isArray(data);

export function Score(props: ScoreProps) {
  const { isDisabled = false } = props;
  const [scoreInfo, setScoreInfo] = useState({
    inputType: 'JSON',
    inputFile: null as File | null,
    inputData: null as object | null,
    scoreData: null as object | null,
    loading: false
  });
  const [scoreError, setScoreError] = useState<any>(null);
  const [uploadFileTsetShowPdfoS3] = useUploadFileToS3Mutation();
  const [uploadInternalAction] = useUploadInternalActionMutation();
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();

  const [searchParams] = useSearchParams();
  const aiworkderId = searchParams.get('aiworkerId');

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScoreInfo(prev => ({ ...prev, loading: true, scoreData: null, inputFile: file }));
    setScoreError(null);
    try {
      // Read the file content using FileReader
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });

      // Parse the JSON content
      const jsonData = JSON.parse(fileContent);

      const generatePayLoad = [
        {
          "filename": file.name,
          "source_type": "data"
        }
      ];

      const uploadURL: any = await getAiworkersPresignedUrl(generatePayLoad).unwrap();
      const source_id = uploadURL?.[0]?.source_id;

      await uploadFileTsetShowPdfoS3({
        file: file,
        presignedUrl: uploadURL?.[0]?.presigned_url,
        CtyShowToast,
      }).unwrap();

      const integrationPayload = {
        "name": "claims_score",
        payload: {
          "aiworker_id": aiworkderId,
          'source_id': source_id,
          "source_type": "data",
        }
      };
      const jsonResponse = await uploadInternalAction(integrationPayload).unwrap();

      let partsArray: { Part: string; Quantity: number; }[] = [];
      if (jsonResponse && isArrayData((jsonResponse as any)?.['Estimated Parts with Quantity'])) {
        const partsObject = (jsonResponse as any)['Estimated Parts with Quantity'][0];
        partsArray = Object.entries(partsObject).map(([key, value]) => ({
          Part: key,
          Quantity: value as number
        }));
      }

      const processedResponse = {
        ...(typeof jsonResponse === 'object' ? jsonResponse : {}),
      };

      setScoreInfo(prev => ({
        ...prev, inputData: jsonData,
        scoreData: {
          totalScore: (jsonResponse as any)?.['claim_score'],
          claimNumber: (jsonResponse as any)?.['claim_no'],
          outputData: processedResponse,
        }
      }));
    } catch (error) {
      CtyShowToast('error', (error as any)?.message || 'Failed to process Score');
      setScoreError(error);
    } finally {
      setScoreInfo(prev => ({ ...prev, loading: false }));
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';  
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const ClaimScore = (scoreInfo?.scoreData as any)?.outputData[0]?.claim_score;
  const ClaimNumber = (scoreInfo?.scoreData as any)?.outputData[0]?.claim_no;

  return (
    <div className="flex flex-col sm:w-[100%] md:w-auto lg:w-auto xl:w-auto bg-default-100 h-full overflow-y-auto p-3 gap-3 lg:flex-row min-h-screen mb-4">
      {/* Viewer Section */}
      <div className="flex-1 lg:w-1/2 overflow-y-auto">
        <div className="bg-content1 rounded-lg shadow-sm p-4 lg:p-6 mb-4">
          <h1 className="text-xl lg:text-2xl font-bold text-default-700 mb-4 lg:mb-6">Claim Input</h1>

          <div className="flex flex-col lg:flex-row gap-4 mb-4 lg:mb-6">
            <div className="w-full lg:flex-1 lg:min-w-[140px]">
              <Select
                label="Input Type"
                placeholder="Select Input Type"
                className="max-w-xs"
                isDisabled={isDisabled}
                onChange={(e) => setScoreInfo(prev => ({ ...prev, inputType: e.target.value }))}
                defaultSelectedKeys={[scoreInfo?.inputType]}
              >
                <SelectItem value="JSON" key={'JSON'} id="json-option">JSON</SelectItem>
                <SelectItem value="API" key={'API'} id="api-option">API</SelectItem>
              </Select>
            </div>

            <div className="w-full lg:flex-1 lg:self-end lg:w-[50%]">
              {!scoreInfo?.inputFile ? (
                <label className={`flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-default-300 rounded-lg transition-colors ${isDisabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:border-primary hover:bg-primary/10'}`}>
                  <div className="flex flex-col items-center">
                    <Icon icon="material-symbols:upload" />
                    <p className="text-sm text-default-500">
                      <span className="font-semibold">Upload JSON</span>
                    </p>
                    <p className="text-xs text-default-500">JSON files only</p>
                  </div>
                  <Input
                    type="file"
                    id="upload-json-input"
                    accept="application/json"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg">
                  <span className="flex-1 text-sm text-primary truncate" title={scoreInfo?.inputFile.name}>
                    {scoreInfo?.inputFile.name?.length > 50
                      ? `${scoreInfo.inputFile.name.slice(0, 50)}...`
                      : scoreInfo?.inputFile.name}
                  </span>
                  <CtyButton
                    onPress={() => setScoreInfo(prev => ({
                      ...prev,
                      inputFile: null,
                      inputData: null,
                      scoreData: null
                    }))}
                    className="p-1 hover:bg-primary/10 rounded-full transition-colors min-w-0"
                    variant='flat'
                    color="white"
                  >
                    <Icon icon="radix-icons:cross-2" />
                  </CtyButton>
                </div>
              )}
            </div>
          </div>

          {scoreInfo?.inputData && (
            <div className="sm:min-h-[400px] bg-default-100 rounded-lg overflow-auto lg:h-[70vh]">
              <JsonFormBuilder
                jsonData={scoreInfo?.inputData}
                readOnly={true}
                onChange={() => { }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Data Display Section */}
      <div className="flex-1 p-4 mb-16 lg:w-1/2 overflow-y-auto border-t lg:border-l lg:border-t-0 border-default-200">
        {scoreError?.message ? <div>
          <h2 className="text-lg p-2 lg:text-xl font-semibold text-default-700 lg:mb-6">Error</h2>
          <div className="rounded-lg shadow-sm bg-danger/10 p-4 text-danger">
            <p className="text-sm lg:text-base">{scoreError.message}</p>
          </div>
        </div> : <div className="rounded-lg shadow-sm relative bg-content1">
          <h2 className="text-lg p-2 lg:text-xl font-semibold text-default-700 lg:mb-6">Claim Score</h2>

          {scoreInfo?.loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-content1 bg-opacity-80 z-50">
              <CtySpinner size="lg" />
            </div>
          )}

          <div className="overflow-y-auto">
            {(scoreInfo?.scoreData && (scoreInfo.scoreData as any).outputData) ?
              (
                <>
                  <div className="bg-default-100 rounded-xl p-4 mb-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-foreground mb-1">
                          {ClaimScore ?? 0}%
                      </div>
                      <div className="text-sm text-default-500 mb-3">
                          Claim Number: {ClaimNumber ?? 'Unknown'}
                      </div>
                      <Progress
                          value={ClaimScore ?? 0}
                          color={getScoreColor(ClaimScore ?? 0)}
                        className="w-full"
                        size="md"
                      />
                    </div>
                  </div>
                  <JsonFormBuilder
                    jsonData={(scoreInfo.scoreData as any).outputData}
                    readOnly={true}
                    onChange={() => { }}
                  />
                </>
              )
              : !scoreInfo?.loading && scoreInfo?.inputFile ? (
                <div className="flex flex-col items-center justify-center py-8 text-default-500">
                  <p className="text-sm lg:text-base">Processing data...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-default-500">
                  <p className="text-sm lg:text-base">Provide JSON or API inputs to see the score</p>
                </div>
              )}
          </div>
        </div>}
      </div>
    </div>
  );
}

export default Score;
